import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download, Beef, Users, CalendarDays, Loader2, ChevronRight, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { relativeDate } from "@/lib/format";

const TYPE_COLOR = {
  "Saddle Bronc Horse": "#E8833A",
  "Bareback Horse": "#D4A359",
  Bull: "#FF6B4A",
};

const STATUS_DOT = {
  done: "bg-emerald-500 text-emerald-400",
  running: "bg-[#E8833A] text-[#E8833A]",
  error: "bg-red-500 text-red-400",
  idle: "bg-[#3A3D48] text-[#686B77]",
};

export default function StockHub() {
  const nav = useNavigate();
  const [tab, setTab] = useState("animals");
  const [stats, setStats] = useState(null);
  const [animals, setAnimals] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [contractor, setContractor] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [importingRP, setImportingRP] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [st, a, c, e] = await Promise.all([
      api.stockStats(), api.listAnimals({}), api.listContractors(), api.listEvents(),
    ]);
    setStats(st);
    setAnimals(a);
    setContractors(c);
    setEvents(e);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.listAnimals({}).then(setAnimals).catch(() => {});
      api.listContractors().then(setContractors).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.stockSeed();
      toast.success(`Imported ${r.total_animals} animals · ${r.total_contractors} contractors`);
      await load();
    } catch {
      toast.error("Stock import failed");
    }
    setSeeding(false);
  };

  const importRP = async () => {
    setImportingRP(true);
    try {
      const r = await api.stockImportRodeopilot();
      toast.success(`Imported ${r.imported} head from Rodeo Pilot (${r.upserted} new)`);
      await load();
    } catch {
      toast.error("Rodeo Pilot import failed");
    }
    setImportingRP(false);
  };

  const filteredAnimals = useMemo(() => {
    return animals.filter((a) => {
      const okQ = !q || a.name.toLowerCase().includes(q.toLowerCase());
      const okT = !type || a.animal_type === type;
      const okC = !contractor || a.contractor === contractor;
      return okQ && okT && okC;
    });
  }, [animals, q, type, contractor]);

  return (
    <div className="max-w-[1700px] mx-auto px-5 md:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E8833A] mb-2">Rodeo Stock Intelligence</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-[#F3EFE6] leading-[1.05]">
            The animals & the outfits<span className="text-[#E8833A]"> behind them.</span>
          </h1>
          <p className="text-[#B8BAC4] text-base mt-3 max-w-2xl">
            Bucking horses and bulls from the CFR & Maple Leaf Circuit Finals, their owners and stock
            contractors — with verified public news on each. Open any animal or outfit to gather intel.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <button
            data-testid="import-rodeopilot-button"
            onClick={importRP}
            disabled={importingRP}
            className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors disabled:opacity-60"
          >
            {importingRP ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Import full season (Rodeo Pilot)
          </button>
          <button
            data-testid="stock-seed-button"
            onClick={seed}
            disabled={seeding}
            className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm bg-[#1A1D26] border border-[#E8833A]/40 text-[#F3EFE6] hover:bg-[#22262F] transition-colors disabled:opacity-60"
          >
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-[#E8833A]" />}
            CFR + Maple Leaf
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { icon: Beef, label: "Animals", val: stats?.animals },
          { icon: Users, label: "Contractors", val: stats?.contractors },
          { icon: CalendarDays, label: "Events", val: stats?.events },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-[#262933] bg-[#12141A] p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#1A1D26] border border-[#262933] flex items-center justify-center text-[#E8833A]">
              <s.icon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display font-extrabold text-2xl text-[#F3EFE6] leading-none">{s.val ?? "—"}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#9A9CA6] mt-1">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-[#262933]">
        {[
          { k: "animals", l: `Animals (${animals.length})` },
          { k: "contractors", l: `Contractors (${contractors.length})` },
          { k: "events", l: `Events (${events.length})` },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`stock-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={cn(
              "px-4 py-3 text-sm font-medium transition-colors duration-200 border-b-2 -mb-px",
              tab === t.k ? "border-[#E8833A] text-[#F3EFE6]" : "border-transparent text-[#9A9CA6] hover:text-[#F3EFE6]"
            )}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#686B77]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
        </div>
      ) : animals.length === 0 && tab !== "events" ? (
        <div className="text-center py-20 rounded-xl border border-dashed border-[#262933] bg-[#0F1116] text-[#9A9CA6]">
          No stock yet. Click “Import CFR + Maple Leaf stock” above.
        </div>
      ) : tab === "animals" ? (
        <>
          <div className="flex flex-col gap-3 mb-6">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#686B77] pointer-events-none" />
              <Input
                data-testid="animal-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search animals…"
                className="w-full h-11 pl-10 bg-[#12141A] border-[#262933] text-[#F3EFE6] placeholder:text-[#686B77] focus-visible:ring-[#E8833A]"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Chip active={!type} onClick={() => setType("")}>All types</Chip>
              {(stats?.types || []).map((t) => (
                <Chip key={t} active={type === t} onClick={() => setType(t)}>{t}</Chip>
              ))}
              <span className="w-px h-5 bg-[#262933] mx-1" />
              <Chip active={!contractor} onClick={() => setContractor("")}>All outfits</Chip>
              {(stats?.contractor_names || []).map((c) => (
                <Chip key={c} active={contractor === c} onClick={() => setContractor(c)}>{c}</Chip>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredAnimals.map((a) => (
              <button
                key={a.id}
                data-testid="animal-card"
                onClick={() => nav(`/animal/${a.id}`)}
                className="text-left rounded-xl border border-[#262933] bg-[#12141A] p-5 hover:border-[#E8833A]/40 hover:-translate-y-1 transition-transform duration-200 group"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-[#F3EFE6] text-lg leading-tight">{a.name}</h3>
                  <ChevronRight className="h-5 w-5 text-[#686B77] group-hover:text-[#E8833A] transition-colors shrink-0" />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider font-semibold border"
                    style={{ color: TYPE_COLOR[a.animal_type], borderColor: `${TYPE_COLOR[a.animal_type]}44`, backgroundColor: `${TYPE_COLOR[a.animal_type]}14` }}>
                    {a.animal_type}
                  </span>
                  <span className="font-mono text-[10px] text-[#686B77]">{a.brand_code} {a.brand_number}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-[#1E212A] flex items-center justify-between">
                  <span className="text-xs text-[#9A9CA6] truncate">{a.contractor}</span>
                  <span className={cn("h-2 w-2 rounded-full shrink-0", (STATUS_DOT[a.discovery_status] || STATUS_DOT.idle).split(" ")[0])} />
                </div>
                {(a.appearances_count || a.total_won > 0) && (
                  <div className="mt-1.5 font-mono text-[10px] text-[#686B77]">
                    {a.appearances_count || 0} draws
                    {a.total_won > 0 && ` · $${Math.round(a.total_won).toLocaleString()} won`}
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      ) : tab === "contractors" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contractors.map((c) => (
            <button
              key={c.id}
              data-testid="contractor-card"
              onClick={() => nav(`/contractor/${c.id}`)}
              className="text-left rounded-xl border border-[#262933] bg-[#12141A] p-5 hover:border-[#E8833A]/40 hover:-translate-y-1 transition-transform duration-200 group"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-bold text-[#F3EFE6] text-lg leading-tight">{c.name}</h3>
                <ChevronRight className="h-5 w-5 text-[#686B77] group-hover:text-[#E8833A] transition-colors shrink-0" />
              </div>
              <div className="mt-2 font-mono text-[11px] text-[#9A9CA6]">Brands: {(c.brands || []).join(", ")}</div>
              <div className="mt-3 pt-3 border-t border-[#1E212A] flex items-center justify-between">
                <span className="text-xs text-[#E8833A] font-semibold">{c.animal_count} head</span>
                {c.last_discovery ? (
                  <span className="font-mono text-[10px] text-[#686B77] flex items-center gap-1"><Clock className="h-3 w-3" />{relativeDate(c.last_discovery)}</span>
                ) : (
                  <span className={cn("h-2 w-2 rounded-full", (STATUS_DOT[c.discovery_status] || STATUS_DOT.idle).split(" ")[0])} />
                )}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {events.map((e) => (
            <div key={e.id} data-testid="event-card" className="rounded-xl border border-[#262933] bg-[#12141A] p-6">
              <h3 className="font-display font-bold text-2xl text-[#F3EFE6]">{e.name}</h3>
              <div className="flex items-center gap-1.5 mt-2 text-sm text-[#9A9CA6]">
                <MapPin className="h-4 w-4" /> {e.location}
              </div>
              <div className="flex items-center gap-1.5 mt-1 text-sm text-[#9A9CA6]">
                <CalendarDays className="h-4 w-4" /> {e.dates}
              </div>
              <div className="mt-4 pt-4 border-t border-[#1E212A] space-y-1.5 text-sm">
                <div><span className="text-[#686B77] font-mono text-xs uppercase tracking-wider">Main Stock Contractor</span><div className="text-[#F3EFE6]">{e.main_stock_contractor}</div></div>
                <div><span className="text-[#686B77] font-mono text-xs uppercase tracking-wider">Timed Event Stock Contractor</span><div className="text-[#F3EFE6]">{e.timed_event_stock_contractor}</div></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={cn(
      "px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider border transition-colors duration-200",
      active ? "bg-[#E8833A] text-[#0B0D11] border-[#E8833A]" : "bg-[#12141A] text-[#9A9CA6] border-[#262933] hover:text-[#F3EFE6]"
    )}
  >
    {children}
  </button>
);
